import { useEffect, useState } from 'react';
import type { PartnerApplication, PartnerApplicationStatus, PartnerCommissionModel, PartnerStatus, PartnerType } from '@accestra/types';
import {
  Button,
  C,
  Card,
  Field,
  Pill,
  TableWrap,
  friendlyError,
  formatMaybeDateTime,
  inputStyle,
  paFetch,
  statusColor,
  tdS,
  textareaStyle,
  thS,
  titleCase,
  toNullableNumber,
  asNullableText,
} from './shared';

const APPLICATION_STATUS_OPTIONS: Array<PartnerApplicationStatus | 'all'> = [
  'all',
  'new',
  'reviewing',
  'approved',
  'rejected',
  'converted',
];

const PARTNER_TYPE_OPTIONS: PartnerType[] = [
  'agency',
  'consultant',
  'accessibility_specialist',
  'reseller',
  'other',
];

const COMMISSION_MODEL_OPTIONS: PartnerCommissionModel[] = [
  'recurring_percent',
  'one_time_percent',
  'one_time_fixed',
];

const PARTNER_STATUS_OPTIONS: PartnerStatus[] = [
  'active',
  'paused',
  'applicant',
];

interface ApplicationListResponse {
  applications: PartnerApplication[];
  total: number;
}

interface ApplicationDetailResponse {
  application: PartnerApplication;
}

function emptyConvertForm() {
  return {
    partnerType: 'agency' as PartnerType,
    commissionModel: 'recurring_percent' as PartnerCommissionModel,
    commissionPercent: '20',
    commissionDurationMonths: '12',
    legalName: '',
    contactName: '',
    phone: '',
    notes: '',
    status: 'active' as PartnerStatus,
  };
}

export default function PartnerApplicationsPanel() {
  const [statusFilter, setStatusFilter] = useState<PartnerApplicationStatus | 'all'>('all');
  const [applications, setApplications] = useState<PartnerApplication[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedApplication, setSelectedApplication] = useState<PartnerApplication | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [convertForm, setConvertForm] = useState(emptyConvertForm);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyAction, setBusyAction] = useState<string | null>(null);

  async function loadApplications() {
    setLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams({
        page: '1',
        pageSize: '50',
      });
      if (statusFilter !== 'all') {
        query.set('status', statusFilter);
      }

      const result = await paFetch<ApplicationListResponse>('GET', `/platform-admin/partner-applications?${query.toString()}`);
      setApplications(result.applications);
      if (!result.applications.find((application) => application.id === selectedId)) {
        setSelectedId(result.applications[0]?.id ?? null);
      }
    } catch (nextError) {
      setError(friendlyError(nextError));
    } finally {
      setLoading(false);
    }
  }

  async function loadApplicationDetail(applicationId: string) {
    try {
      const result = await paFetch<ApplicationDetailResponse>('GET', `/platform-admin/partner-applications/${applicationId}`);
      setSelectedApplication(result.application);
    } catch (nextError) {
      setError(friendlyError(nextError));
    }
  }

  useEffect(() => {
    void loadApplications();
  }, [statusFilter]);

  useEffect(() => {
    if (selectedId) {
      void loadApplicationDetail(selectedId);
    } else {
      setSelectedApplication(null);
    }
  }, [selectedId]);

  useEffect(() => {
    if (!selectedApplication) {
      return;
    }

    setReviewNotes(selectedApplication.reviewNotes ?? '');
    setConvertForm({
      partnerType: selectedApplication.partnerType ?? 'agency',
      commissionModel: 'recurring_percent',
      commissionPercent: '20',
      commissionDurationMonths: '12',
      legalName: selectedApplication.company ?? '',
      contactName: selectedApplication.name ?? '',
      phone: '',
      notes: selectedApplication.notes ?? '',
      status: 'active',
    });
  }, [selectedApplication]);

  async function handleReviewAction(kind: 'reviewing' | 'approve' | 'reject') {
    if (!selectedApplication) {
      return;
    }

    setBusyAction(kind);
    setFeedback(null);
    setError(null);
    try {
      await paFetch(
        'POST',
        `/platform-admin/partner-applications/${selectedApplication.id}/${kind}`,
        { reviewNotes: asNullableText(reviewNotes) },
      );
      await loadApplications();
      await loadApplicationDetail(selectedApplication.id);
      setFeedback(
        kind === 'approve'
          ? 'Application approved.'
          : kind === 'reject'
            ? 'Application rejected.'
            : 'Application marked reviewing.',
      );
    } catch (nextError) {
      setError(friendlyError(nextError));
    } finally {
      setBusyAction(null);
    }
  }

  async function handleConvert() {
    if (!selectedApplication) {
      return;
    }

    setBusyAction('convert');
    setFeedback(null);
    setError(null);
    try {
      await paFetch(
        'POST',
        `/platform-admin/partner-applications/${selectedApplication.id}/convert`,
        {
          partnerType: convertForm.partnerType,
          commissionModel: convertForm.commissionModel,
          commissionPercent: toNullableNumber(convertForm.commissionPercent),
          commissionDurationMonths: toNullableNumber(convertForm.commissionDurationMonths),
          legalName: asNullableText(convertForm.legalName),
          contactName: asNullableText(convertForm.contactName),
          phone: asNullableText(convertForm.phone),
          notes: asNullableText(convertForm.notes),
          status: convertForm.status,
        },
      );
      await loadApplications();
      await loadApplicationDetail(selectedApplication.id);
      setFeedback('Application converted into a partner record.');
    } catch (nextError) {
      setError(friendlyError(nextError));
    } finally {
      setBusyAction(null);
    }
  }

  function renderMaybeLink(value: string | null | undefined) {
    if (!value) {
      return 'Not provided';
    }

    return (
      <a
        href={value}
        target="_blank"
        rel="noreferrer"
        style={{ color: C.accent, textDecoration: 'none', fontWeight: 600 }}
      >
        {value}
      </a>
    );
  }

  return (
    <div className="acc-platform-admin-split acc-platform-admin-split--partner-applications">
      <Card
        title="Partner applications"
        subtitle="Filter inbound applications by status and open one for founder review."
        action={(
          <div style={{ display: 'flex', gap: 8 }}>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as PartnerApplicationStatus | 'all')}
              style={inputStyle}
            >
              {APPLICATION_STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status === 'all' ? 'All statuses' : titleCase(status)}
                </option>
              ))}
            </select>
            <Button onClick={() => void loadApplications()}>Refresh</Button>
          </div>
        )}
      >
        {loading ? (
          <div style={{ color: C.muted, fontSize: 13 }}>Loading applications…</div>
        ) : (
          <TableWrap>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thS}>Applicant</th>
                  <th style={thS}>Company</th>
                  <th style={thS}>Status</th>
                  <th style={thS}>Created</th>
                </tr>
              </thead>
              <tbody>
                {applications.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ ...tdS, textAlign: 'center', color: C.muted }}>
                      No partner applications found.
                    </td>
                  </tr>
                ) : applications.map((application) => (
                  <tr
                    key={application.id}
                    onClick={() => setSelectedId(application.id)}
                    style={{
                      cursor: 'pointer',
                      background: selectedId === application.id ? 'rgba(14, 165, 164, 0.06)' : 'transparent',
                    }}
                  >
                    <td style={tdS}>
                      <div style={{ fontWeight: 760 }}>{application.name}</div>
                      <div style={{ color: C.muted, fontSize: 12 }}>{application.email}</div>
                    </td>
                    <td style={tdS}>{application.company}</td>
                    <td style={tdS}>
                      <Pill label={titleCase(application.status)} tone={statusColor(application.status)} />
                    </td>
                    <td style={tdS}>{formatMaybeDateTime(application.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Card>

      <div className="acc-platform-admin-detail-stack">
        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: '10px 12px', color: C.danger, fontSize: 13 }}>
            {error}
          </div>
        )}
        {feedback && (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '10px 12px', color: C.success, fontSize: 13 }}>
            {feedback}
          </div>
        )}

        {!selectedApplication ? (
          <Card
            title="Founder review"
            subtitle="Select an application to review its commercial context, decision notes, and conversion settings."
          >
            <div className="acc-platform-admin-empty">
              <div style={{ fontSize: 15, fontWeight: 760, color: C.text }}>No application selected</div>
              <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.65 }}>
                Choose an applicant from the list to open the review workflow.
              </div>
            </div>
          </Card>
        ) : (
          <>
            <Card
              title={selectedApplication.company}
              subtitle="Founder-operated review summary for approval, rejection, and clean partner conversion."
            >
              <div className="acc-platform-admin-entity-header">
                <div>
                  <div style={{ fontSize: 18, fontWeight: 780, color: C.text }}>{selectedApplication.name}</div>
                  <div style={{ color: C.muted, fontSize: 13, marginTop: 4 }}>{selectedApplication.email}</div>
                </div>
                <Pill label={titleCase(selectedApplication.status)} tone={statusColor(selectedApplication.status)} />
              </div>

              <div className="acc-platform-admin-summary-grid">
                <div className="acc-platform-admin-summary-card">
                  <span>Submitted</span>
                  <strong>{formatMaybeDateTime(selectedApplication.createdAt)}</strong>
                </div>
                <div className="acc-platform-admin-summary-card">
                  <span>Country</span>
                  <strong>{selectedApplication.country || 'Not provided'}</strong>
                </div>
                <div className="acc-platform-admin-summary-card">
                  <span>Partner type</span>
                  <strong>{selectedApplication.partnerType ? titleCase(selectedApplication.partnerType) : 'Not provided'}</strong>
                </div>
                <div className="acc-platform-admin-summary-card">
                  <span>Current outcome</span>
                  <strong>{selectedApplication.convertedPartnerId ? 'Converted to partner' : titleCase(selectedApplication.status)}</strong>
                </div>
              </div>
            </Card>

            <Card
              title="Application context"
              subtitle="Keep the applicant’s footprint, audience, and route to market separate from the decision controls."
            >
              <div className="acc-platform-admin-summary-grid">
                <div className="acc-platform-admin-summary-card">
                  <span>Website</span>
                  <strong>{renderMaybeLink(selectedApplication.websiteUrl)}</strong>
                </div>
                <div className="acc-platform-admin-summary-card">
                  <span>LinkedIn</span>
                  <strong>{renderMaybeLink(selectedApplication.linkedinUrl)}</strong>
                </div>
                <div className="acc-platform-admin-summary-card">
                  <span>Application notes</span>
                  <strong>{selectedApplication.notes || 'Not provided'}</strong>
                </div>
              </div>

              <div className="acc-platform-admin-copy-grid">
                <div className="acc-platform-admin-copy-card">
                  <div className="acc-platform-admin-copy-card__title">Audience</div>
                  <div className="acc-platform-admin-copy-card__body">
                    {selectedApplication.audienceDescription || 'Not provided'}
                  </div>
                </div>
                <div className="acc-platform-admin-copy-card">
                  <div className="acc-platform-admin-copy-card__title">How they plan to sell</div>
                  <div className="acc-platform-admin-copy-card__body">
                    {selectedApplication.howTheyPlanToSell || 'Not provided'}
                  </div>
                </div>
              </div>
            </Card>

            <Card
              title="Review decision"
              subtitle="Capture founder notes once, then move the application forward with clear primary and secondary actions."
            >
              <Field label="Review notes">
                <textarea
                  style={{ ...textareaStyle, minHeight: 128 }}
                  value={reviewNotes}
                  onChange={(event) => setReviewNotes(event.target.value)}
                />
              </Field>

              <div className="acc-platform-admin-action-row">
                <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, maxWidth: 520 }}>
                  Use “Mark reviewing” to hold the application in founder triage, approve when it is ready for conversion, and keep rejection visually separate from the primary decision path.
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <Button
                    disabled={busyAction !== null}
                    onClick={() => void handleReviewAction('reviewing')}
                  >
                    {busyAction === 'reviewing' ? 'Saving…' : 'Mark reviewing'}
                  </Button>
                  <Button
                    variant="primary"
                    disabled={busyAction !== null}
                    onClick={() => void handleReviewAction('approve')}
                  >
                    {busyAction === 'approve' ? 'Approving…' : 'Approve'}
                  </Button>
                  <Button
                    variant="danger"
                    disabled={busyAction !== null}
                    onClick={() => void handleReviewAction('reject')}
                  >
                    {busyAction === 'reject' ? 'Rejecting…' : 'Reject'}
                  </Button>
                </div>
              </div>
            </Card>

            <Card
              title="Convert to partner"
              subtitle="Keep partner creation settings isolated so the conversion step feels deliberate instead of crowded into review notes."
            >
              {(selectedApplication.status === 'approved' || selectedApplication.status === 'converted') ? (
                <div style={{ display: 'grid', gap: 16 }}>
                  {selectedApplication.convertedPartnerId && (
                    <div style={{ color: C.muted, fontSize: 12 }}>
                      Converted partner id: {selectedApplication.convertedPartnerId}
                    </div>
                  )}
                  <div className="acc-platform-admin-toolbar">
                    <Field label="Partner type">
                      <select
                        style={inputStyle}
                        value={convertForm.partnerType}
                        onChange={(event) => setConvertForm((current) => ({ ...current, partnerType: event.target.value as PartnerType }))}
                      >
                        {PARTNER_TYPE_OPTIONS.map((option) => <option key={option} value={option}>{titleCase(option)}</option>)}
                      </select>
                    </Field>
                    <Field label="Partner status">
                      <select
                        style={inputStyle}
                        value={convertForm.status}
                        onChange={(event) => setConvertForm((current) => ({ ...current, status: event.target.value as PartnerStatus }))}
                      >
                        {PARTNER_STATUS_OPTIONS.map((option) => <option key={option} value={option}>{titleCase(option)}</option>)}
                      </select>
                    </Field>
                    <Field label="Commission model">
                      <select
                        style={inputStyle}
                        value={convertForm.commissionModel}
                        onChange={(event) => setConvertForm((current) => ({ ...current, commissionModel: event.target.value as PartnerCommissionModel }))}
                      >
                        {COMMISSION_MODEL_OPTIONS.map((option) => <option key={option} value={option}>{titleCase(option)}</option>)}
                      </select>
                    </Field>
                  </div>

                  <div className="acc-platform-admin-toolbar">
                    <Field label="Commission percent">
                      <input
                        style={inputStyle}
                        value={convertForm.commissionPercent}
                        onChange={(event) => setConvertForm((current) => ({ ...current, commissionPercent: event.target.value }))}
                      />
                    </Field>
                    <Field label="Commission duration (months)">
                      <input
                        style={inputStyle}
                        value={convertForm.commissionDurationMonths}
                        onChange={(event) => setConvertForm((current) => ({ ...current, commissionDurationMonths: event.target.value }))}
                      />
                    </Field>
                    <Field label="Legal name">
                      <input
                        style={inputStyle}
                        value={convertForm.legalName}
                        onChange={(event) => setConvertForm((current) => ({ ...current, legalName: event.target.value }))}
                      />
                    </Field>
                  </div>

                  <div className="acc-platform-admin-toolbar">
                    <Field label="Contact name">
                      <input
                        style={inputStyle}
                        value={convertForm.contactName}
                        onChange={(event) => setConvertForm((current) => ({ ...current, contactName: event.target.value }))}
                      />
                    </Field>
                    <Field label="Phone">
                      <input
                        style={inputStyle}
                        value={convertForm.phone}
                        onChange={(event) => setConvertForm((current) => ({ ...current, phone: event.target.value }))}
                      />
                    </Field>
                  </div>

                  <Field label="Partner notes">
                    <textarea
                      style={textareaStyle}
                      value={convertForm.notes}
                      onChange={(event) => setConvertForm((current) => ({ ...current, notes: event.target.value }))}
                    />
                  </Field>

                  <div className="acc-platform-admin-action-row">
                    <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
                      Conversion creates the internal partner record using the founder-controlled defaults shown here.
                    </div>
                    <Button
                      variant="primary"
                      disabled={busyAction !== null}
                      onClick={() => void handleConvert()}
                    >
                      {busyAction === 'convert' ? 'Converting…' : 'Convert to partner'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="acc-platform-admin-empty">
                  <div style={{ fontSize: 15, fontWeight: 760, color: C.text }}>Awaiting approval</div>
                  <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.65 }}>
                    Approve the application before converting it into a partner record.
                  </div>
                </div>
              )}
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
